/**
 * Стек undo/redo правок знаков Визуализатора (command inverse + remap id).
 */

export const VISUALIZER_SIGN_HISTORY_MAX = 50;

/** Сдвиг копии знака (~15 м на средних широтах). */
export const VISUALIZER_SIGN_COPY_OFFSET_DEG = 0.00015;

export type VisualizerHistoryEntry = {
  undo: () => Promise<void>;
  redo: () => Promise<void>;
};

/** Свойства знака, достаточные для recreate / style snapshot. */
export type VisualizerSignSnapshotProps = {
  id: number;
  kind?: "point" | "line" | "polygon" | "text";
  source?: string;
  klsId?: number | null;
  symbolAssetId?: number | null;
  sizePx?: number;
  rotationDeg?: number;
  strokeColor?: string;
  strokeWidth?: number;
  strokeDash?: "solid" | "dash" | "dot" | "dash_dot";
  fillColor?: string;
  fillOpacity?: number;
  label?: string;
  objectId?: number | null;
  layerGroupId?: number | null;
  isLocked?: boolean;
  isVisible?: boolean;
};

export type VisualizerSignSnapshot = {
  type: "Feature";
  id?: number;
  geometry: GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon;
  properties: VisualizerSignSnapshotProps;
};

export class VisualizerSignHistory {
  private entries: VisualizerHistoryEntry[] = [];
  /** Индекс последней применённой записи; -1 = ничего не применено. */
  private index = -1;
  private readonly idMap = new Map<number, number>();

  constructor(private readonly maxDepth: number = VISUALIZER_SIGN_HISTORY_MAX) {}

  clear(): void {
    this.entries = [];
    this.index = -1;
    this.idMap.clear();
  }

  get canUndo(): boolean {
    return this.index >= 0;
  }

  get canRedo(): boolean {
    return this.index < this.entries.length - 1;
  }

  get depth(): number {
    return this.entries.length;
  }

  get cursor(): number {
    return this.index;
  }

  push(entry: VisualizerHistoryEntry): void {
    this.entries = this.entries.slice(0, this.index + 1);
    this.entries.push(entry);
    while (this.entries.length > this.maxDepth) {
      this.entries.shift();
    }
    this.index = this.entries.length - 1;
  }

  async undo(): Promise<boolean> {
    if (!this.canUndo) return false;
    const entry = this.entries[this.index];
    this.index -= 1;
    await entry.undo();
    return true;
  }

  async redo(): Promise<boolean> {
    if (!this.canRedo) return false;
    this.index += 1;
    const entry = this.entries[this.index];
    await entry.redo();
    return true;
  }

  /** Актуальный id знака с учётом recreate после undo/redo. */
  resolveId(id: number): number {
    let cur = id;
    const seen = new Set<number>();
    while (this.idMap.has(cur) && !seen.has(cur)) {
      seen.add(cur);
      cur = this.idMap.get(cur)!;
    }
    return cur;
  }

  /** Зафиксировать, что знак `from` теперь существует как `to`. */
  remapId(from: number, to: number): void {
    if (from === to) return;
    this.idMap.set(from, to);
    for (const [key, value] of [...this.idMap.entries()]) {
      if (value === from) this.idMap.set(key, to);
    }
  }
}

/** Тело POST create из снимка feature. */
export function featureToCreatePayload(feature: VisualizerSignSnapshot): Record<string, unknown> {
  const p = feature.properties;
  const kind = p.kind ?? "point";
  const base: Record<string, unknown> = {
    kind,
    size_px: p.sizePx ?? 32,
    rotation_deg: p.rotationDeg ?? 0,
    stroke_color: p.strokeColor ?? "#2563eb",
    stroke_width: p.strokeWidth ?? 2,
    stroke_dash: p.strokeDash ?? "solid",
    fill_color: p.fillColor ?? p.strokeColor ?? "#2563eb",
    fill_opacity: p.fillOpacity ?? 0.2,
    label: p.label || null,
    object_id: p.objectId ?? null,
  };

  if (p.isLocked != null) {
    base.is_locked = p.isLocked;
  }
  if (p.isVisible != null) {
    base.is_visible = p.isVisible;
  }

  if (kind === "text") {
    const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
    return { ...base, source: "text", lng, lat, label: (p.label || "").trim() };
  }

  const source = p.source === "asset" ? "asset" : "kls";
  base.source = source;
  base.kls_id = source === "kls" ? (p.klsId ?? null) : null;
  base.symbol_asset_id = source === "asset" ? (p.symbolAssetId ?? null) : null;

  if (feature.geometry.type === "Point" || kind === "point") {
    const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
    return { ...base, kind: "point", lng, lat };
  }
  if (feature.geometry.type === "LineString" || kind === "line") {
    return {
      ...base,
      kind: "line",
      coordinates: (feature.geometry as GeoJSON.LineString).coordinates,
    };
  }
  const ring = (feature.geometry as GeoJSON.Polygon).coordinates[0] ?? [];
  const coords =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;
  return { ...base, kind: "polygon", coordinates: coords };
}

/** Поля PATCH для отката стиля/позиции. */
export function featureToStylePatch(feature: VisualizerSignSnapshot): Record<string, unknown> {
  const p = feature.properties;
  const patch: Record<string, unknown> = {
    size_px: p.sizePx ?? 32,
    rotation_deg: p.rotationDeg ?? 0,
    stroke_color: p.strokeColor ?? "#2563eb",
    stroke_width: p.strokeWidth ?? 2,
    stroke_dash: p.strokeDash ?? "solid",
    fill_color: p.fillColor ?? p.strokeColor ?? "#2563eb",
    fill_opacity: p.fillOpacity ?? 0.2,
    label: p.label ?? "",
  };
  if (p.objectId == null) {
    patch.clear_object = true;
  } else {
    patch.object_id = p.objectId;
  }
  if (p.layerGroupId == null) {
    patch.clear_layer_group = true;
  } else {
    patch.layer_group_id = p.layerGroupId;
  }
  if (p.isLocked != null) {
    patch.is_locked = p.isLocked;
  }
  if (p.isVisible != null) {
    patch.is_visible = p.isVisible;
  }
  if (feature.geometry.type === "Point") {
    const [lng, lat] = feature.geometry.coordinates;
    patch.lng = lng;
    patch.lat = lat;
  } else if (feature.geometry.type === "LineString") {
    patch.coordinates = feature.geometry.coordinates;
  } else {
    const ring = feature.geometry.coordinates[0] ?? [];
    const coords =
      ring.length > 1 &&
      ring[0][0] === ring[ring.length - 1][0] &&
      ring[0][1] === ring[ring.length - 1][1]
        ? ring.slice(0, -1)
        : ring;
    patch.coordinates = coords;
  }
  return patch;
}

export function asSignSnapshot(feature: {
  type: string;
  id?: number | string;
  geometry: GeoJSON.Geometry;
  properties?: Record<string, unknown> | null;
}): VisualizerSignSnapshot | null {
  if (!feature.geometry || feature.geometry.type === "GeometryCollection") return null;
  const props = (feature.properties ?? {}) as Record<string, unknown>;
  const id = Number(props.id ?? feature.id);
  if (!Number.isFinite(id)) return null;
  return {
    type: "Feature",
    id,
    geometry: feature.geometry as GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon,
    properties: {
      id,
      kind: props.kind as VisualizerSignSnapshotProps["kind"],
      source: props.source as string | undefined,
      klsId: props.klsId as number | null | undefined,
      symbolAssetId: props.symbolAssetId as number | null | undefined,
      sizePx: props.sizePx as number | undefined,
      rotationDeg: props.rotationDeg as number | undefined,
      strokeColor: props.strokeColor as string | undefined,
      strokeWidth: props.strokeWidth as number | undefined,
      strokeDash: props.strokeDash as VisualizerSignSnapshotProps["strokeDash"],
      fillColor: props.fillColor as string | undefined,
      fillOpacity: props.fillOpacity as number | undefined,
      label: props.label as string | undefined,
      objectId: props.objectId as number | null | undefined,
      layerGroupId: props.layerGroupId as number | null | undefined,
      isLocked: props.isLocked as boolean | undefined,
      isVisible: props.isVisible as boolean | undefined,
    },
  };
}

function shiftPosition(
  position: GeoJSON.Position,
  dLng: number,
  dLat: number,
): GeoJSON.Position {
  const next = position.slice() as GeoJSON.Position;
  next[0] = Number(position[0]) + dLng;
  next[1] = Number(position[1]) + dLat;
  return next;
}

/** Сдвиг геометрии знака для видимой копии (все вершины). */
export function shiftSignGeometry(
  geometry: GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon,
  dLng: number = VISUALIZER_SIGN_COPY_OFFSET_DEG,
  dLat: number = 0,
): GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon {
  if (geometry.type === "Point") {
    return {
      type: "Point",
      coordinates: shiftPosition(geometry.coordinates, dLng, dLat),
    };
  }
  if (geometry.type === "LineString") {
    return {
      type: "LineString",
      coordinates: geometry.coordinates.map((p) => shiftPosition(p, dLng, dLat)),
    };
  }
  return {
    type: "Polygon",
    coordinates: geometry.coordinates.map((ring) =>
      ring.map((p) => shiftPosition(p, dLng, dLat)),
    ),
  };
}

/** Снимок знака со смещённой геометрией для create-копии. */
export function snapshotForCopy(
  feature: VisualizerSignSnapshot,
  dLng: number = VISUALIZER_SIGN_COPY_OFFSET_DEG,
  dLat: number = 0,
): VisualizerSignSnapshot {
  return {
    ...feature,
    geometry: shiftSignGeometry(feature.geometry, dLng, dLat),
  };
}
